import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, ShieldCheck, Zap, Headphones, Sparkles, Star, ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";

const trustStats = [
  { k: "120k+", v: "Orders delivered" },
  { k: "1-2h", v: "Median delivery" },
  { k: "99.9%", v: "Working guarantee" },
  { k: "24/7", v: "Human support" },
];

const iconMap = {
  ott: "🎬", ai: "✦", productivity: "◆", vpn: "◈", music: "♪", learning: "◇", design: "◆", gaming: "▲",
};

export default function HomePage() {
  const [featured, setFeatured] = useState([]);
  const [trending, setTrending] = useState([]);
  const [categories, setCategories] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    api.get("/products", { params: { featured: true, limit: 8 } }).then((r) => setFeatured(r.data));
    api.get("/products", { params: { trending: true, limit: 6 } }).then((r) => setTrending(r.data));
    api.get("/categories").then((r) => setCategories(r.data));
    api.get("/testimonials").then((r) => setTestimonials(r.data));
    api.get("/faqs").then((r) => setFaqs(r.data));
  }, []);

  return (
    <div className="grain">
      {/* HERO */}
      <section className="relative overflow-hidden noise-radial">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-40 -left-20 w-[600px] h-[600px] rounded-full bg-[#FF4433]/10 blur-[120px] animate-glow-pulse" />
          <div className="absolute top-40 -right-40 w-[500px] h-[500px] rounded-full bg-[#3d40ff]/5 blur-[100px]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 pt-16 md:pt-24 pb-20 md:pb-32">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-4xl">
            <div className="inline-flex items-center gap-2 border border-white/10 rounded-full px-3 py-1.5 mb-8 bg-white/5 backdrop-blur">
              <span className="w-2 h-2 rounded-full bg-[#FF4433] animate-pulse" />
              <span className="text-xs font-medium">Trusted by 120,000+ subscribers worldwide</span>
            </div>
            <h1 className="font-display font-black text-5xl md:text-7xl lg:text-[92px] leading-[0.9] tracking-tighter text-kyro-ink">
              Every premium <br />subscription, <span className="text-gradient-coral">one vault.</span>
            </h1>
            <p className="mt-8 text-lg md:text-xl text-kyro-mute max-w-2xl leading-relaxed">
              Netflix, ChatGPT Plus, Notion, Spotify, VPNs — verified accounts delivered within 1–2 hours,
              backed by human support that actually replies.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Link to="/shop" data-testid="hero-shop-cta" className="kyro-btn-primary inline-flex items-center gap-2">
                Enter the vault <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/#featured" data-testid="hero-featured-cta" className="kyro-btn-secondary inline-flex items-center gap-2">
                Featured drops <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>

          {/* Trust stat strip */}
          <div className="mt-16 md:mt-24 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10 border-t border-white/5 pt-10">
            {trustStats.map((s) => (
              <div key={s.v}>
                <div className="font-display text-3xl md:text-4xl font-black tracking-tight">{s.k}</div>
                <div className="text-sm text-kyro-mute mt-1">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VALUE PROPS */}
      <section className="relative max-w-7xl mx-auto px-5 md:px-8 py-16">
        <div className="grid md:grid-cols-3 gap-4">
          {[
            { icon: Zap, title: "Quick delivery", body: "Credentials in your inbox within 1–2 hours of a confirmed payment." },
            { icon: ShieldCheck, title: "Verified sources", body: "Every subscription is checked, encrypted, and monitored 24/7." },
            { icon: Headphones, title: "24/7 human support", body: "Real humans on chat and email — no chatbots, no scripts." },
          ].map((v, i) => (
            <motion.div key={v.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }} className="kyro-card p-8 kyro-hover-lift">
              <v.icon className="w-6 h-6 text-[#FF4433] mb-6" strokeWidth={1.5} />
              <h3 className="font-display font-bold text-xl mb-2">{v.title}</h3>
              <p className="text-sm text-kyro-mute leading-relaxed">{v.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-16">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="kyro-overline">The catalog</div>
            <h2 className="font-display font-black text-4xl md:text-5xl tracking-tighter mt-3">Browse by category</h2>
          </div>
          <Link to="/categories" className="hidden md:inline-flex items-center gap-1 text-sm text-kyro-mute hover:text-kyro-ink">
            All categories <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {categories.map((c, i) => (
            <motion.div key={c.slug} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.04 }}>
              <Link to={`/shop?category=${c.slug}`} data-testid={`cat-tile-${c.slug}`} className="block kyro-card p-6 kyro-hover-lift">
                <div className="text-2xl mb-4 font-display text-[#FF4433]">{iconMap[c.slug] || "◇"}</div>
                <div className="font-display font-bold text-lg">{c.name}</div>
                <div className="text-xs text-kyro-mute mt-1">{c.tagline}</div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section id="featured" className="max-w-7xl mx-auto px-5 md:px-8 py-16">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="kyro-overline">Featured</div>
            <h2 className="font-display font-black text-4xl md:text-5xl tracking-tighter mt-3">Editor's picks</h2>
          </div>
          <Link to="/shop?featured=1" className="hidden md:inline-flex items-center gap-1 text-sm text-kyro-mute hover:text-kyro-ink">
            View all <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {featured.map((p, i) => <ProductCard key={p.id} p={p} i={i} />)}
        </div>
      </section>

      {/* TRENDING BENTO */}
      {trending.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 md:px-8 py-16">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="kyro-overline flex items-center gap-2"><Sparkles className="w-3.5 h-3.5" /> Trending</div>
              <h2 className="font-display font-black text-4xl md:text-5xl tracking-tighter mt-3">Hot right now</h2>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {trending.slice(0, 6).map((p, i) => <ProductCard key={p.id} p={p} i={i} />)}
          </div>
        </section>
      )}

      {/* TESTIMONIALS */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-24">
        <div className="max-w-2xl">
          <div className="kyro-overline">Reviews</div>
          <h2 className="font-display font-black text-4xl md:text-5xl tracking-tighter mt-3">Loved by careful buyers</h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
          {testimonials.map((t, i) => (
            <motion.div key={t.id} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }} className="kyro-card p-6 kyro-hover-lift">
              <div className="flex gap-1 mb-4">
                {Array.from({ length: t.rating || 5 }).map((_, j) => (
                  <Star key={j} className="w-3.5 h-3.5 fill-[#FF4433] text-[#FF4433]" />
                ))}
              </div>
              <p className="text-sm leading-relaxed mb-6">&ldquo;{t.quote}&rdquo;</p>
              <div>
                <p className="font-semibold text-sm">{t.name}</p>
                <p className="text-xs text-kyro-faint">{t.role}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-4xl mx-auto px-5 md:px-8 py-24">
        <div className="max-w-2xl mb-10">
          <div className="kyro-overline">Answers</div>
          <h2 className="font-display font-black text-4xl md:text-5xl tracking-tighter mt-3">Frequently asked</h2>
        </div>
        <div className="space-y-3">
          {faqs.map((f, i) => {
            const isOpen = openFaq === f.id;
            return (
              <div key={f.id} className="kyro-card">
                <button
                  data-testid={`faq-${i}`}
                  onClick={() => setOpenFaq(isOpen ? null : f.id)}
                  className="w-full flex items-center justify-between text-left px-6 py-5"
                >
                  <span className="font-semibold text-base pr-4">{f.question}</span>
                  <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${isOpen ? "rotate-180 text-[#FF4433]" : "text-kyro-mute"}`} />
                </button>
                {isOpen && (
                  <div className="px-6 pb-6 -mt-1 text-sm text-kyro-mute leading-relaxed">{f.answer}</div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-16">
        <div className="kyro-card p-10 md:p-16 relative overflow-hidden">
          <div className="absolute -right-10 -top-10 w-72 h-72 rounded-full bg-[#FF4433]/20 blur-3xl" />
          <div className="relative">
            <div className="kyro-overline mb-4">Ready?</div>
            <h3 className="font-display font-black text-3xl md:text-5xl tracking-tighter leading-tight max-w-3xl">Stop juggling logins. Start owning your stack.</h3>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/shop" data-testid="footer-cta-shop" className="kyro-btn-primary inline-flex items-center gap-2">Open the vault <ArrowRight className="w-4 h-4" /></Link>
              <Link to="/track" data-testid="footer-cta-track" className="kyro-btn-secondary">Track my order</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
